import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
/**
 * Render do bloco de oferta do Botão de Ação ("abaixo do vídeo").
 *
 * Espelho React do que o player embed desenha em vanilla — a fidelidade visual
 * entre os dois vale mais que a elegância do JSX aqui.
 *
 * O componente não sabe quais são os 8 modelos: ele lê `spec.layout` e desenha
 * slot a slot, na ordem declarada. Modelo novo = entrada nova em `presets.ts`,
 * zero mudança neste arquivo.
 */

import React, { useState } from 'react';
import type {
  ActionButton,
  ActionButtonTemplate,
  ActionButtonTextElement,
  ActionButtonToggleableText,
} from '@/store/videoFeatures/types';
import {
  BUTTON_SHADOW,
  TEMPLATE_MAX_WIDTH,
  buttonGeometry,
  modelSpec,
  textSizePx,
  textWeight,
  type ActionButtonSlot,
  type ActionButtonTextElementKey,
} from '@/lib/action-button';
import { ensureHttps } from '@/utils/url';

interface ActionButtonBelowProps {
  actionButton: ActionButton;
  /** `isSecondary` diz qual caminho foi clicado (aceite ou recusa/downsell). */
  onClick?: (isSecondary?: boolean) => void;
}

/** Espaço vertical entre os slots do bloco. */
const SLOT_GAP = 12;

/** Altura do trilho da barra de progresso. */
const PROGRESS_HEIGHT = 18;

/** Largura do trilho. Menor que a do bloco para a barra não encostar nas bordas. */
const PROGRESS_WIDTH = '90%';

/** Preenchimento assumido quando o dado não veio. Igual ao slider do editor. */
const PROGRESS_FALLBACK = 80;

export const ActionButtonBelow: React.FC<ActionButtonBelowProps> = ({
  actionButton,
  onClick,
}) => {
  useLanguage();
  // Um estado só, mas com identidade do alvo: com um boolean os dois botões
  // hoverizariam juntos.
  const [hovered, setHovered] = useState<'primary' | 'secondary' | null>(null);

  if (!actionButton.enabled) return null;

  const template: ActionButtonTemplate = actionButton.template || {};
  const spec = modelSpec(template.model);

  // ---------------------------------------------------------------------------
  // Botão primário — vive fora do template, em `text/url/size/style.*`, que é o
  // que o modo "sobre o vídeo" e os vídeos legados também usam.
  // ---------------------------------------------------------------------------

  const getAnimationClass = () => {
    switch (actionButton.animation) {
      case 'pulse':
        return 'animate-pulse';
      case 'bounce':
        return 'animate-bounce';
      case 'shake':
        return 'animate-shake';
      default:
        return '';
    }
  };

  // Prioriza `style.*` (formato novo) sobre as props de raiz (legado).
  const bgColor =
    actionButton.style?.backgroundColor ||
    actionButton.backgroundColor ||
    actionButton.buttonColor ||
    '#1D4FFF';
  const textColor = actionButton.style?.textColor || actionButton.textColor || '#FFFFFF';
  const hoverBgColor = actionButton.style?.hoverBackgroundColor || bgColor;
  const hoverTextColor = actionButton.style?.hoverTextColor || textColor;
  // `!= null` e não truthy: raio 0 (canto reto) é escolha válida do usuário e
  // não pode cair no fallback.
  const borderRadius =
    actionButton.style?.borderRadius != null
      ? `${actionButton.style.borderRadius}px`
      : (actionButton.style?.rounded || actionButton.rounded)
        ? '9999px'
        : '0.375rem';

  // Efeitos: sombra default ligada (espelha o player, que já aplicava sombra
  // fixa antes do toggle existir); pulso é opt-in.
  const hasShadow = actionButton.style?.shadow !== false;
  const hasPulse = actionButton.style?.pulse === true;

  const geometry = buttonGeometry(actionButton.size);

  // ---------------------------------------------------------------------------
  // Navegação
  // ---------------------------------------------------------------------------

  const openLink = (url: string | undefined, newTab: boolean) => {
    if (!url) return; // Sem link não abre nada — o clique só notifica o pai.
    window.open(ensureHttps(url), newTab ? '_blank' : '_self');
  };

  const handlePrimaryClick = () => {
    onClick?.(false);
    // `!== false` e não `=== true`: o botão sempre abriu em nova aba antes do
    // campo existir, então ausente continua significando nova aba.
    openLink(actionButton.url || actionButton.link, template.openInNewTab !== false);
  };

  const handleSecondaryClick = () => {
    onClick?.(true);
    openLink(template.secondary?.url, template.secondary?.openInNewTab === true);
  };

  // ---------------------------------------------------------------------------
  // Slots
  // ---------------------------------------------------------------------------

  /**
   * Render genérico de elemento textual. Todo elemento tem a mesma forma
   * (`text/color/size/weight`), então um helper só cobre os oito.
   */
  const renderText = (
    key: ActionButtonTextElementKey,
    element: ActionButtonTextElement | ActionButtonToggleableText | undefined,
    extraStyle?: React.CSSProperties,
  ) => {
    if (!element?.text) return null;
    // Só `urgency` e `strikethrough` carregam `enabled`; nos demais é undefined.
    if ((element as ActionButtonToggleableText).enabled === false) return null;

    return (
      <div
        style={{
          width: '100%',
          fontSize: textSizePx(key, element.size),
          fontWeight: textWeight(element.weight),
          color: element.color,
          lineHeight: 1.35,
          ...extraStyle,
        }}
      >
        {element.text}
      </div>
    );
  };

  const renderProgressBar = () => {
    const bar = template.progressBar;
    if (!bar || bar.enabled === false) return null;

    // `??` e não `||`: 0% é valor válido e não pode virar o default.
    const percentage = Math.min(100, Math.max(0, bar.percentage ?? PROGRESS_FALLBACK));

    return (
      <div
        style={{
          position: 'relative',
          width: PROGRESS_WIDTH,
          height: PROGRESS_HEIGHT,
          borderRadius: 9999,
          backgroundColor: bar.trackColor,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            borderRadius: 9999,
            backgroundColor: bar.fillColor,
            transition: 'width 300ms ease',
          }}
        />
        {bar.showText !== false && bar.text && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              color: bar.textColor,
              whiteSpace: 'nowrap',
            }}
          >
            {bar.text}
          </div>
        )}
      </div>
    );
  };

  const renderButtons = () => {
    const secondary = template.secondary;
    // O modelo diz SE existe segundo caminho; `renderAs` (editável no card de
    // Conteúdo) diz a FORMA. Mesma resolução dos cards do editor.
    const secondaryAs = spec.secondaryAs ? secondary?.renderAs || spec.secondaryAs : null;
    // Sem texto não há o que clicar — é como o usuário desliga o segundo caminho
    // sem mexer no toggle.
    const showSecondary = !!secondaryAs && secondary?.enabled !== false && !!secondary?.text;
    const twoButtons = showSecondary && secondaryAs === 'button';
    const inline = twoButtons && spec.buttonsInline;

    const primaryHovered = hovered === 'primary';
    const primaryStyle: React.CSSProperties = {
      minHeight: geometry.height,
      padding: geometry.padding,
      minWidth: inline ? geometry.minWidthSplit : geometry.minWidth,
      fontSize: actionButton.fontSize || geometry.fontSize,
      backgroundColor: primaryHovered ? hoverBgColor : bgColor,
      color: primaryHovered ? hoverTextColor : textColor,
      borderRadius,
      // Mesmos valores do player (hostvsl-player.js / runner.js).
      boxShadow: hasShadow
        ? primaryHovered
          ? BUTTON_SHADOW.hover
          : BUTTON_SHADOW.normal
        : 'none',
      animation: hasPulse ? 'actionButtonPulse 2500ms ease-in-out infinite' : undefined,
    };

    const primaryButton = (
      <button
        // hover:scale-105 sai quando o pulso está ligado: as duas coisas animam
        // `transform` e brigariam entre si.
        className={`${getAnimationClass()} font-medium transition-all cursor-pointer ${hasPulse ? '' : 'hover:scale-105'}`}
        style={primaryStyle}
        onClick={handlePrimaryClick}
        onMouseEnter={() => setHovered('primary')}
        onMouseLeave={() => setHovered(null)}
      >
        {actionButton.text || translateInterfaceText("Clique Aqui")}
      </button>
    );

    let secondaryNode: React.ReactNode = null;

    if (twoButtons) {
      const isHovered = hovered === 'secondary';
      secondaryNode = (
        <button
          className="transition-all cursor-pointer"
          style={{
            minHeight: geometry.height,
            padding: geometry.padding,
            minWidth: inline ? geometry.minWidthSplit : geometry.minWidth,
            fontSize: textSizePx('secondary', secondary.size),
            fontWeight: textWeight(secondary.weight, 'semibold'),
            color: isHovered ? secondary.hoverColor || secondary.color : secondary.color,
            backgroundColor: isHovered
              ? secondary.hoverBackgroundColor || secondary.backgroundColor
              : secondary.backgroundColor,
            border: `1px solid ${
              (isHovered ? secondary.hoverBorderColor || secondary.borderColor : secondary.borderColor) ||
              'transparent'
            }`,
            borderRadius,
          }}
          onClick={handleSecondaryClick}
          onMouseEnter={() => setHovered('secondary')}
          onMouseLeave={() => setHovered(null)}
        >
          {secondary.text}
        </button>
      );
    } else if (showSecondary) {
      const isHovered = hovered === 'secondary';
      secondaryNode = (
        <span
          role="button"
          tabIndex={0}
          className="transition-colors cursor-pointer"
          style={{
            fontSize: textSizePx('secondary', secondary.size),
            fontWeight: textWeight(secondary.weight),
            color: isHovered ? secondary.hoverColor || secondary.color : secondary.color,
            textDecoration: secondary.underline !== false ? 'underline' : 'none',
            lineHeight: 1.4,
          }}
          onClick={handleSecondaryClick}
          onMouseEnter={() => setHovered('secondary')}
          onMouseLeave={() => setHovered(null)}
        >
          {secondary.text}
        </span>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          // Inline vale só para dois botões lado a lado; o wrap protege o mobile.
          flexDirection: inline ? 'row' : 'column',
          flexWrap: inline ? 'wrap' : 'nowrap',
          gap: inline ? 12 : 10,
        }}
      >
        {primaryButton}
        {secondaryNode}
      </div>
    );
  };

  /**
   * Um `switch` sobre o SLOT é o render genérico; um `switch` sobre o MODELO
   * seria o contrário do que a tabela declarativa existe para evitar.
   */
  const renderSlot = (slot: ActionButtonSlot): React.ReactNode => {
    switch (slot) {
      case 'urgency':
        return renderText('urgency', template.urgency);
      case 'progressBar':
        return renderProgressBar();
      case 'headline':
        return renderText('headline', template.headline);
      case 'subheadline':
        return renderText('subheadline', template.subheadline);
      case 'offerText':
        return renderText('offerText', template.offerText);
      case 'strikethrough':
        return renderText('strikethrough', template.strikethrough, {
          textDecoration: 'line-through',
        });
      case 'featured':
        return renderText('featured', template.featured);
      case 'price':
        return renderText('price', template.price);
      case 'priceNote':
        return renderText('priceNote', template.priceNote);
      case 'buttons':
        return renderButtons();
      // 'secondary' é chave de escala tipográfica, não slot desenhável: o
      // segundo caminho sai junto com 'buttons'.
      default:
        return null;
    }
  };

  return (
    <div className="w-full flex justify-center">
      <style>{`
        @keyframes actionButtonPulse {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div
        // Sem cor de fundo de propósito: o bloco vive na página do cliente, e um
        // fundo fixo quebraria tanto o dark mode do preview quanto o site destino.
        style={{
          width: '100%',
          maxWidth: TEMPLATE_MAX_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: SLOT_GAP,
          textAlign: 'center',
        }}
      >
        {spec.layout.map((slot) => (
          <React.Fragment key={slot}>{renderSlot(slot)}</React.Fragment>
        ))}
      </div>
    </div>
  );
};
